import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { trackLang } from '../pp/track';

// The announcement bar (guided P&P phase 6, 2026-10-17; backend/announcements.py). Fernando: "an Announcement bar that
// we can use to provide critical info like HRX has to be ordered 9:00 today" — everyone sees it, no "Got it", it stays
// until it ends. In the reader's language: on a shared screen the language of the person working there (trackLang),
// otherwise the login's own (the server reads My profile). Shown in the app shell, on the P&P station and the cart
// tablet's list — never over the picking walk or the packing scan (it would cover their buttons).

interface Act { label: string; button: string; endpoint: string; body: Record<string, unknown> }
interface A { id: number; severity: 'info' | 'warning' | 'urgent'; text: string; actions?: Act[] }

const CLS: Record<string, string> = {
  urgent: 'bg-red-700 text-white border-red-500',
  warning: 'bg-amber-500 text-black border-amber-300',
  info: 'bg-blue-900 text-blue-50 border-blue-700',
};

export default function AnnouncementBar({ className = '' }: { className?: string }) {
  const [items, setItems] = useState<A[]>([]);
  const [all, setAll] = useState(false);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    const load = () => {
      const lang = trackLang();
      authFetch(`${API}/announcements/active${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`)
        .then(r => (r.ok ? r.json() : null)).then(d => { if (alive && d) setItems(d.announcements || []); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('focus', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('focus', load); };
  }, [tick]);
  // a manager's button under an automatic notice (21.10: "Found" on the Can't-find notice) — the server names it
  const act = async (x: Act) => {
    if (busy) return;
    setBusy(x.label);
    try {
      const r = await authFetch(`${API}${x.endpoint}`, { method: 'POST', body: JSON.stringify(x.body) });
      if (!r.ok) { const d = await r.json().catch(() => ({} as any)); alert(d.detail || `Could not do it (HTTP ${r.status})`); }
    } finally { setBusy(''); setTick(k => k + 1); }
  };
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, 3);
  return (
    <div className={`space-y-1 ${className}`}>
      {shown.map(a => (
        <div key={a.id} className={`px-4 py-2 rounded-lg border text-sm font-semibold ${CLS[a.severity] || CLS.info}`}>
          {a.severity === 'urgent' ? '⚠ ' : ''}{a.text}
          {(a.actions || []).map(x => (
            <div key={x.label} className="mt-1 flex flex-wrap items-center gap-2 font-normal">
              <span>{x.label}</span>
              <button onClick={() => act(x)} disabled={!!busy}
                className="px-2.5 py-0.5 rounded-md bg-white text-black text-xs font-bold disabled:opacity-50">
                {busy === x.label ? '…' : `✓ ${x.button}`}</button>
            </div>
          ))}
        </div>
      ))}
      {items.length > 3 && (
        <button onClick={() => setAll(v => !v)} className="text-xs text-gray-400 underline">
          {all ? 'Show fewer' : `+${items.length - 3} more`}</button>
      )}
    </div>
  );
}
